import { useState, useEffect } from "react";
import { sendFeedbackApi, trackEvent } from "../api/reviewApi";
import { getTokenSession, saveTokenSession } from "../utils/session";

export const useFeedbackFlow = (token, isValidToken) => {
  const [step, setStep] = useState("RATING");
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [response, setResponse] = useState(null);
  const [feedbackId, setFeedbackId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Restore session on load
  useEffect(() => {
    if (!token || !isValidToken) return;

    const tokenSession = getTokenSession(token);
    if (!tokenSession) return;

    setFeedbackId(tokenSession.feedbackId || null);
    setRating(tokenSession.rating || null);
    setResponse(tokenSession.response || null);
    setStep(tokenSession.completed ? "RESULT" : tokenSession.step || "RATING");
  }, [token, isValidToken]);

  const handleRatingClick = async (value) => {
    try {
      setRating(value);

      const data = await sendFeedbackApi(
        { rating: value, comment: null, email: null, source: "QR" },
        token
      );

      setFeedbackId(data.feedbackId);

      if (value === 3) {
        setResponse(data);
        setStep("RESULT");
      } else {
        setStep("COMMENT");
      }

      saveTokenSession(token, {
        feedbackId: data.feedbackId,
        rating: value,
        step: value === 3 ? "RESULT" : "COMMENT",
        response: value === 3 ? data : null,
        qrTracked: true,
      });
    } catch (err) {
      console.error("Send feedback error:", err);
      alert("Something went wrong. Please try again.");
    }
  };

  const handleSubmitComment = async () => {
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      alert("Invalid email");
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const data = await sendFeedbackApi(
        { feedbackId, rating, comment: comment || null, email: email || null, source: "QR" },
        token
      );

      setResponse(data);
      setStep("RESULT");

      saveTokenSession(token, {
        feedbackId,
        rating,
        step: "RESULT",
        response: data,
        qrTracked: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleClick = async () => {
    window.open(response.redirectUrl, "_blank");

    try {
      await trackEvent({ eventType: "GOOGLE_CLICK", source: "QR" }, token);
    } catch (err) {
      console.error("Tracking failed", err);
    }

    const newResponse = { action: "THANK_YOU" };
    setResponse(newResponse);
    setStep("RESULT");

    saveTokenSession(token, {
      feedbackId,
      rating,
      step: "RESULT",
      response: newResponse,
      completed: true,
      qrTracked: true,
    });
  };

  return {
    step,
    rating,
    comment, setComment,
    email, setEmail,
    response,
    isSubmitting,
    handleRatingClick,
    handleSubmitComment,
    handleGoogleClick,
  };
};